import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { catchError, distinctUntilChanged, finalize, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { arabicTitleValidator, englishTitleValidator } from '../../../../shared/validators/localized-title.validators';
import { markPendingChangesSaved } from '../../../../shared/guards/pending-changes.guard';
import { apiErrorFallback, extractApiErrorMessage } from '../../../../core/http/extract-api-error-message';
import { Translation } from '../../../../core/i18n/translation.service';
import type { AssetDetailsDto } from '../../../../core/models/assets/assets.dto';
import type { BranchDdlDto } from '../../../../core/models/branches/branch-ddl.dto';
import type { AssetCategoryDto } from '../../../../core/models/assets/assets.dto';
import { AssetsService } from '../../../../core/services/assets.service';
import { BranchesService } from '../../../../core/services/branches.service';
import { AdminBackLinkComponent } from '../../../../shared/components/admin-back-link/admin-back-link.component';
import { WnDatePickerComponent } from '../../../../shared/components/wn-date-picker/wn-date-picker.component';
import { WnRequiredFieldMarkerDirective } from '../../../../shared/directives/wn-required-field-marker.directive';
import { ContentLanguageService, applyBilingualRequired } from '../../../../core/i18n/content-language';
import { pickLocalizedTitle, titleErrorKey } from './asset-titles';

import { WnFocusInvalidOnSubmitDirective } from '../../../../shared/directives/wn-focus-invalid-on-submit.directive';
@Component({
  selector: 'app-asset-editor-page',
  standalone: true,
  imports: [WnFocusInvalidOnSubmitDirective, WnRequiredFieldMarkerDirective, ReactiveFormsModule, RouterLink, TranslatePipe, ButtonModule, AdminBackLinkComponent, WnDatePickerComponent],
  templateUrl: './asset-editor-page.component.html',
  styleUrl: './asset-editor-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetEditorPageComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly assets = inject(AssetsService);
  private readonly branches = inject(BranchesService);
  private readonly translation = inject(Translation);
  private readonly contentLang = inject(ContentLanguageService);
  /** WN-926: bilingual fields render in tenant-language order (primary first). */
  protected readonly langOrder = this.contentLang.order();

  protected readonly assetId = signal<string | null>(null);
  protected readonly loading = signal(false);
  protected readonly submitting = signal(false);
  protected readonly loadError = signal<string | null>(null);
  protected readonly apiError = signal<string | null>(null);
  protected readonly categories = signal<AssetCategoryDto[]>([]);
  protected readonly branchOptions = signal<BranchDdlDto[]>([]);

  protected readonly form = this.fb.group({
    assetCode: this.fb.control('', { validators: [Validators.required] }),
    titleEn: this.fb.control('', { validators: [Validators.required, englishTitleValidator()] }),
    titleAr: this.fb.control(''),
    assetCategoryId: this.fb.control('', { validators: [Validators.required] }),
    currentBranchId: this.fb.control(''),
    serialNumber: this.fb.control(''),
    purchaseDate: this.fb.control(''),
    purchaseCost: this.fb.control<number | null>(null),
    warrantyExpiryDate: this.fb.control(''),
    notes: this.fb.control(''),
  });

  constructor() {
    applyBilingualRequired(this.contentLang.primary(), this.form.controls.titleAr, this.form.controls.titleEn);
    this.loadLookups();
    this.route.paramMap
      .pipe(
        map((pm) => pm.get('id')),
        distinctUntilChanged(),
        tap(() => {
          this.loadError.set(null);
          this.apiError.set(null);
        }),
        switchMap((id) => {
          this.assetId.set(id);
          if (!id) {
            this.resetForCreate();
            return of(null);
          }
          this.loading.set(true);
          return this.assets.getAsset(id).pipe(
            catchError((err) => {
              this.loadError.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
              return of(null);
            }),
            finalize(() => this.loading.set(false)),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((asset) => {
        if (asset) this.patchFromAsset(asset);
      });
  }

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const body = {
      assetCode: raw.assetCode.trim(),
      titleAr: raw.titleAr.trim(),
      titleEn: raw.titleEn.trim(),
      assetCategoryId: raw.assetCategoryId,
      serialNumber: this.nullIfEmpty(raw.serialNumber),
      purchaseDate: this.nullIfEmpty(raw.purchaseDate),
      purchaseCost: raw.purchaseCost,
      warrantyExpiryDate: this.nullIfEmpty(raw.warrantyExpiryDate),
      currentBranchId: this.nullIfEmpty(raw.currentBranchId),
      notes: this.nullIfEmpty(raw.notes),
    };

    this.submitting.set(true);
    this.apiError.set(null);
    const id = this.assetId();
    const request = id ? this.assets.updateAsset({ id, ...body }) : this.assets.createAsset(body);
    request.pipe(finalize(() => this.submitting.set(false))).subscribe({
      next: (asset) => {
        markPendingChangesSaved(this);
        void this.router.navigate(['/assets', asset.id]);
      },
      error: (err) => this.apiError.set(extractApiErrorMessage(err, apiErrorFallback(this.translation))),
    });
  }

  protected categoryTitle(category: AssetCategoryDto): string {
    return pickLocalizedTitle(category.titleEn, category.titleAr, this.translation.getCurrentLang().toLowerCase().startsWith('ar'));
  }

  protected readonly titleErrorKey = titleErrorKey;

  private loadLookups(): void {
    forkJoin({
      categories: this.assets.getActiveCategories().pipe(catchError(() => of([]))),
      branches: this.branches.getBranchesDdl().pipe(catchError(() => of([]))),
    })
      .pipe(takeUntilDestroyed())
      .subscribe(({ categories, branches }) => {
        this.categories.set(categories);
        this.branchOptions.set(branches);
      });
  }

  private resetForCreate(): void {
    this.form.reset({
      assetCode: '',
      titleEn: '',
      titleAr: '',
      assetCategoryId: '',
      currentBranchId: '',
      serialNumber: '',
      purchaseDate: '',
      purchaseCost: null,
      warrantyExpiryDate: '',
      notes: '',
    });
  }

  private patchFromAsset(asset: AssetDetailsDto): void {
    this.form.reset({
      assetCode: asset.assetCode ?? '',
      titleEn: asset.titleEn ?? '',
      titleAr: asset.titleAr ?? '',
      assetCategoryId: asset.assetCategoryId ?? '',
      currentBranchId: asset.currentBranchId ?? '',
      serialNumber: asset.serialNumber ?? '',
      purchaseDate: this.toDateInput(asset.purchaseDate),
      purchaseCost: asset.purchaseCost ?? null,
      warrantyExpiryDate: this.toDateInput(asset.warrantyExpiryDate),
      notes: asset.notes ?? '',
    });
  }

  private toDateInput(value?: string | null): string {
    return value ? value.slice(0, 10) : '';
  }

  private nullIfEmpty(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
}
